import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageShell from "../components/PageShell";
import useSeo from "../hooks/useSeo";
import { images, processSteps, serviceCards, talentCategories } from "../data/content";

export default function Services() {
  useSeo("Recruitment Services", "Explore TalentBridge Consulting services for job posting support and candidate sourcing in Calgary.");

  return (
    <PageShell>
      <section className="bg-white px-5 pb-16 pt-32 md:px-8 lg:pb-24" data-testid="services-hero-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="services-eyebrow">Services</p>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-[#0A2540] sm:text-5xl lg:text-6xl" data-testid="services-title">Focused hiring support for companies.</h1>
            <p className="mt-6 text-base leading-8 text-[#475569] md:text-lg" data-testid="services-description">
              TalentBridge keeps recruitment support practical: job posting help and candidate sourcing. Specialized hiring areas are shown under categories.
            </p>
          </div>
          <div className="overflow-hidden border border-[#E2E8F0]" data-testid="services-image-wrapper">
            <img src={images.services} alt="Abstract corporate talent strategy visualization" className="aspect-[5/3] w-full object-cover" data-testid="services-hero-image" />
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] px-5 py-16 md:px-8 lg:py-24" data-testid="featured-talent-categories-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-6 md:grid-cols-[0.8fr_1.2fr] md:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="featured-talent-eyebrow">Featured Talent Categories</p>
              <h2 className="mt-4 font-heading text-3xl font-black tracking-tight text-[#0A2540] sm:text-4xl" data-testid="featured-talent-title">Candidate categories companies can request.</h2>
            </div>
            <p className="text-base leading-8 text-[#475569]" data-testid="featured-talent-description">
              TalentBridge can help employers prepare job postings and source candidates across practical, in-demand hiring categories.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="featured-talent-grid">
            {talentCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <article key={category.title} className="group border border-[#E2E8F0] bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]" data-testid={`featured-talent-card-${index}`}>
                  <div className="flex items-center gap-4">
                    <span className="grid h-12 w-12 shrink-0 place-items-center bg-[#0A2540] text-white" data-testid={`featured-talent-icon-${index}`}>
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="font-heading text-xl font-black text-[#0A2540]" data-testid={`featured-talent-card-title-${index}`}>{category.title}</h3>
                  </div>
                  <ul className="mt-6 grid gap-3" data-testid={`featured-talent-role-list-${index}`}>
                    {category.roles.map((role) => (
                      <li key={role} className="flex items-center gap-3 text-sm font-semibold text-[#475569]" data-testid={`featured-talent-role-${role.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                        <span className="h-1.5 w-1.5 bg-[#0066FF]" /> {role}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 py-16 md:px-8 lg:py-24" data-testid="services-list-section">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
          {serviceCards.map((service, index) => {
            const Icon = service.icon;
            return (
              <article key={service.title} className="border border-[#E2E8F0] bg-white p-8" data-testid={`services-card-${index}`}>
                <Icon className="h-9 w-9 text-[#0066FF]" />
                <h2 className="mt-6 font-heading text-2xl font-black text-[#0A2540]" data-testid={`services-card-title-${index}`}>{service.title}</h2>
                <p className="mt-4 text-base leading-8 text-[#475569]" data-testid={`services-card-text-${index}`}>{service.text}</p>
                <Link to="/employers" className="mt-7 inline-flex items-center gap-2 font-bold text-[#0066FF] hover:text-[#0052CC]" data-testid={`services-card-cta-${index}`}>
                  Start here <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      <section className="bg-[#0A2540] px-5 py-16 text-white md:px-8 lg:py-24" data-testid="services-method-section">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-200" data-testid="services-method-eyebrow">Operating model</p>
          <h2 className="mt-4 max-w-3xl font-heading text-3xl font-black tracking-tight sm:text-4xl" data-testid="services-method-title">Every engagement starts with the role and ends with organized candidate information.</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-4">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="border border-white/15 bg-white/5 p-6" data-testid={`services-method-step-${index}`}>
                  <Icon className="h-7 w-7 text-blue-200" />
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-slate-300" data-testid={`services-method-number-${index}`}>0{index + 1}</p>
                  <h3 className="mt-2 font-heading text-xl font-black" data-testid={`services-method-title-${index}`}>{step.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-300" data-testid={`services-method-text-${index}`}>{step.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </PageShell>
  );
}